// pages/ServiceDetail.js
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import services, { getServiceBySlug } from '../../data/services';
import { getProjectBySlug } from '../../data/projects';
import PageHeader from '../../components/PageHeader';
import CtaBanner from '../../components/CtaBanner';
import NotFound from '../NotFound/NotFound';
import usePageTitle from '../../hooks/usePageTitle';
import '../Detail.css';

const steps = [
    { title: 'Discovery', text: 'We discuss your goals, users and requirements in a free consultation.' },
    { title: 'Plan & Design', text: 'You get a clear scope, timeline and designs before development starts.' },
    { title: 'Development', text: 'We build in short iterations and share progress with you every week.' },
    { title: 'Launch & Support', text: 'We test, deploy and keep supporting you after go-live.' },
];

const ServiceDetail = () => {
    const { slug } = useParams();
    const service = getServiceBySlug(slug);

    usePageTitle(service ? service.title : 'Service Not Found', service ? service.overview : undefined);

    if (!service) {
        return <NotFound />;
    }

    const Icon = service.icon;
    const relatedProjects = service.relatedProjects.map(getProjectBySlug).filter(Boolean);
    const otherServices = services.filter((item) => item.slug !== service.slug);

    return (
        <>
            <PageHeader
                title={service.title}
                subtitle={service.description}
                breadcrumbs={[{ label: 'Services', to: '/services' }, { label: service.title }]}
            />

            <section className="section detail">
                <div className="container">
                    <div className="detail-layout">
                        <div className="detail-main">
                            <div className="detail-service-icon">
                                <Icon />
                            </div>
                            <h2>Overview</h2>
                            <p>{service.overview}</p>

                            <h2>What's Included</h2>
                            <ul className="detail-list two-col">
                                {service.includes.map((item) => (
                                    <li key={item}>
                                        <FaCheckCircle />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <h2>How We Work</h2>
                            <div className="detail-steps">
                                {steps.map((step, index) => (
                                    <div key={step.title} className="step-card card">
                                        <span className="step-number">{String(index + 1).padStart(2, '0')}</span>
                                        <h4>{step.title}</h4>
                                        <p>{step.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <aside className="detail-sidebar">
                            <div className="card sidebar-card">
                                <h3>Technologies</h3>
                                <div className="portfolio-tech">
                                    {service.technologies.map((tech) => (
                                        <span key={tech} className="tech-tag">{tech}</span>
                                    ))}
                                </div>
                                <Link to="/contact" className="btn sidebar-btn">
                                    Get a Free Quote <FaArrowRight />
                                </Link>
                                <a href="tel:+923098373032" className="btn btn-outline sidebar-btn">
                                    Call +92 309 8373032
                                </a>
                            </div>

                            <div className="card sidebar-card">
                                <h3>Other Services</h3>
                                <ul className="sidebar-links">
                                    {otherServices.map((item) => (
                                        <li key={item.slug}>
                                            <Link to={`/services/${item.slug}`}>
                                                {item.title} <FaArrowRight />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    </div>

                    {relatedProjects.length > 0 && (
                        <div className="detail-related">
                            <h2>Projects Using This Service</h2>
                            <div className="related-grid">
                                {relatedProjects.map((item) => (
                                    <Link key={item.slug} to={`/portfolio/${item.slug}`} className="related-card card">
                                        <img src={item.image} alt={item.title} loading="lazy" />
                                        <div className="related-content">
                                            <span className="related-category">{item.category}</span>
                                            <h4>{item.title}</h4>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <CtaBanner title={`Need ${service.title}?`} />
        </>
    );
};

export default ServiceDetail;
